"use client";

/*
 * ContractReviewTour — guided, tooltip-style tours for the contract-review
 * flow. Two variants:
 *
 *   "intake"    — the "review a supplier contract" form, from file import down
 *                 to the "Generate contract review" button. Clicking Generate
 *                 finishes the tour (see handleGenerate -> tour.finish()).
 *   "dashboard" — the contract-reviews home: stats, filters, new review, list.
 *
 * Each variant auto-starts once for first-time visitors (localStorage, keyed
 * per variant) and can be replayed anytime via the "Take the tour" button.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

type TourVariant = "intake" | "dashboard";

const LS_SEEN: Record<TourVariant, string> = {
  intake: "contract-review-tour-seen",
  dashboard: "contract-review-dashboard-tour-seen",
};

interface TourStep {
  key: string;
  title: string;
  body: string;
  target?: string;
}

const INTAKE_STEPS: TourStep[] = [
  {
    key: "welcome",
    title: "Welcome to Supplier Contract Review",
    body: "This page builds your full clause-by-clause contract review. Drop in a contract, tell Sensei what matters most, then generate — Sensei does the rest and never sends anything outside your review.",
  },
  {
    key: "cr-file",
    title: "Import your contract",
    body: "Drop a PDF, DOCX, legacy DOC, or XLSX here, or click to browse. Scanned documents are read automatically with Google Vision — no manual typing.",
    target: "[data-tour='cr-file']",
  },
  {
    key: "cr-text",
    title: "Contract text",
    body: "The extracted text lands here so you can see exactly what Sensei will review. You can edit it or paste a contract directly if you prefer.",
    target: "[data-tour='cr-text']",
  },
  {
    key: "cr-type",
    title: "Contract type & your side",
    body: "Pick the contract type to load the right clause set and standard positions, and say whether you're the buyer or the supplier — this shapes the whole review.",
    target: "[data-tour='cr-type']",
  },
  {
    key: "cr-focus",
    title: "What matters most",
    body: "Tell Sensei the outcome you care about — for example \"lead time reliability is critical\". Bigger focus areas get deeper scrutiny.",
    target: "[data-tour='cr-focus']",
  },
  {
    key: "cr-constraints",
    title: "Any constraints?",
    body: "Deadline, budget, or relationship factors that affect how hard to push. These temper Sensei's recommendations.",
    target: "[data-tour='cr-constraints']",
  },
  {
    key: "cr-must-not-lose",
    title: "Anything we must not lose?",
    body: "The protections that can never change — existing payment terms, the right to source elsewhere during a stock-out. Sensei flags any redline that touches them.",
    target: "[data-tour='cr-must-not-lose']",
  },
  {
    key: "cr-context",
    title: "Optional context",
    body: "Client name/id and deadline keep reviews organized per engagement. Leave blank if you don't track them.",
    target: "[data-tour='cr-context']",
  },
  {
    key: "cr-generate",
    title: "Generate the review",
    body: "When you're happy, click \"Generate contract review\". Sensei builds the full clause-by-clause review with redlines and negotiation strategy. Clicking it finishes the tour.",
    target: "[data-tour='cr-generate']",
  },
];

const DASHBOARD_STEPS: TourStep[] = [
  {
    key: "crd-welcome",
    title: "Welcome to your contract reviews",
    body: "This is where every contract review you've run lives. Open a review to read its clause-by-clause findings, or start a brand-new one from here.",
  },
  {
    key: "crd-stats",
    title: "Your numbers at a glance",
    body: "Total reviews, how many RED flags are still open across all contracts, and how many are ready for legal sign-off. The bars on each review card update as redlines are resolved.",
    target: "[data-tour='crd-stats']",
  },
  {
    key: "crd-filters",
    title: "Filter what you see",
    body: "Switch between All, Needs review, and Ready, or narrow by contract type — useful when you're juggling many engagements.",
    target: "[data-tour='crd-filters']",
  },
  {
    key: "crd-new",
    title: "Start a new review",
    body: "Click \"+ New review\" to import a contract and configure the review. That tour picks up from the file upload.",
    target: "[data-tour='crd-new']",
  },
  {
    key: "crd-list",
    title: "Your review cards",
    body: "Each card shows the document, side, type, and date. Badges summarize risk (RED / YELLOW / Ready), and every card has Feedback and Delete actions. Click a card to open it.",
    target: "[data-tour='crd-list']",
  },
];

const ACCENT = "#5B6CFF";
const ACCENT2 = "#EC4899";
const CARD_FONT = "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

interface ContractTourApi {
  active: boolean;
  step: number;
  total: number;
  start: () => void;
  finish: () => void;
}

const TourCtx = createContext<ContractTourApi | null>(null);

export function useContractTour(): ContractTourApi | null {
  return useContext(TourCtx);
}

export function ContractTourStartButton({ label = "Take the tour" }: { label?: string }) {
  const api = useContractTour();
  if (!api) return null;
  return (
    <>
      <style>{STYLES}</style>
      <button type="button" className="crt-start-btn" onClick={() => api.start()}>
        <span className="crt-start-icon">▶</span>
        {label}
      </button>
    </>
  );
}

export default function ContractReviewTour({
  children,
  variant = "intake",
}: {
  children: ReactNode;
  variant?: TourVariant;
}) {
  const steps = variant === "dashboard" ? DASHBOARD_STEPS : INTAKE_STEPS;
  const [step, setStep] = useState(-1); // -1 = inactive

  const start = useCallback(() => setStep(0), []);
  const finish = useCallback(() => setStep(-1), []);
  const next = useCallback(
    () => setStep((s) => (s < 0 ? s : s >= steps.length - 1 ? -1 : s + 1)),
    [steps.length],
  );
  const back = useCallback(() => setStep((s) => (s <= 0 ? s : s - 1)), []);

  // First-visit: auto-start once per variant, then mark seen.
  useEffect(() => {
    if (step >= 0) return;
    let seen = false;
    try {
      seen = localStorage.getItem(LS_SEEN[variant]) === "1";
    } catch {
      /* ignore */
    }
    if (seen) return;
    try {
      localStorage.setItem(LS_SEEN[variant], "1");
    } catch {
      /* ignore */
    }
    const id = window.setTimeout(() => setStep(0), 800);
    return () => window.clearTimeout(id);
  }, [step, variant]);

  // Escape/arrow keys control the tour.
  useEffect(() => {
    if (step < 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") finish();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, next, back, finish]);

  const api: ContractTourApi = {
    active: step >= 0,
    step: Math.max(step, 0),
    total: steps.length,
    start,
    finish,
  };

  return (
    <TourCtx.Provider value={api}>
      {children}
      {step >= 0 && (
        <TourOverlay
          variant={variant}
          steps={steps}
          step={step}
          onNext={next}
          onBack={back}
          onSkip={finish}
        />
      )}
    </TourCtx.Provider>
  );
}

interface RectLike { x: number; y: number; w: number; h: number; }

function TourOverlay({ variant, steps, step, onNext, onBack, onSkip }: {
  variant: TourVariant;
  steps: TourStep[];
  step: number;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}) {
  const def = steps[step] ?? steps[0];
  const [rect, setRect] = useState<RectLike | null>(null);

  // Follow the spotlight target (re-measures on scroll/resize).
  useEffect(() => {
    let stop = false;
    let tries = 0;
    let timer = 0;
    let raf = 0;
    if (!def.target) return () => { stop = true; };
    const measure = (el: Element) => {
      if (stop) return;
      const r = el.getBoundingClientRect();
      const next: RectLike = { x: r.left - 8, y: r.top - 8, w: r.width + 16, h: r.height + 16 };
      setRect((prev) => {
        if (
          prev &&
          Math.abs(prev.x - next.x) <= 3 &&
          Math.abs(prev.y - next.y) <= 3 &&
          Math.abs(prev.w - next.w) <= 3 &&
          Math.abs(prev.h - next.h) <= 3
        )
          return prev;
        return next;
      });
      raf = requestAnimationFrame(() => measure(el));
    };
    const tick = () => {
      if (stop) return;
      const el = document.querySelector(def.target!);
      if (!el) {
        if (++tries < 24) {
          timer = window.setTimeout(tick, 260);
          return;
        }
        setRect(null);
        return;
      }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      timer = window.setTimeout(() => measure(el), 460);
    };
    tick();
    return () => {
      stop = true;
      window.clearTimeout(timer);
      window.cancelAnimationFrame(raf);
    };
  }, [step, variant, def.target]);

  const isLast = step >= steps.length - 1;
  const cardW = 380;
  const cardH = 300;

  // Clamp the card to the viewport so its buttons are always reachable
  // (previously a bottom-anchored target could push the card off-screen).
  const clampTop = (y: number) => Math.min(Math.max(y, 16), Math.max(16, window.innerHeight - cardH - 16));
  const clampLeft = (x: number) => Math.min(Math.max(x, 16), Math.max(16, window.innerWidth - cardW - 16));

  let cardTop = Math.max(16, window.innerHeight / 2 - cardH / 2);
  let cardLeft = Math.max(16, window.innerWidth / 2 - cardW / 2);
  if (rect) {
    const below = rect.y + rect.h + 18;
    cardTop = clampTop(below + cardH < window.innerHeight - 16 ? below : rect.y - cardH - 18 - 16);
    cardLeft = clampLeft(rect.x + rect.w / 2 - cardW / 2);
  }

  const pct = Math.round(((step + 1) / steps.length) * 100);
  const eyebrow = variant === "dashboard" ? "Contract reviews tour" : "Contract review tour";

  return (
    <>
      <style>{STYLES}</style>
      <div className="crt-overlay" role="dialog" aria-modal="true" aria-label={def.title} style={{ fontFamily: CARD_FONT }}>
        {rect ? (
          <div
            className="crt-spotlight"
            style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
          />
        ) : (
          <div className="crt-dim" />
        )}

        <div
          className="crt-card"
          style={{
            left: cardLeft,
            top: cardTop,
            width: cardW,
            maxWidth: "92vw",
          }}
        >
          <div className="crt-card-band" />
          <div className="crt-card-body">
            <div className="crt-card-head">
              <div className="crt-step-no">{step + 1}</div>
              <div className="crt-card-progress-wrap">
                <div className="crt-eyebrow">{eyebrow} · step {step + 1} of {steps.length}</div>
                <div className="crt-progress-track">
                  <div className="crt-progress-fill" style={{ width: `${pct}%` }} />
                </div>
              </div>
            </div>

            <div className="crt-title">{def.title}</div>
            <div className="crt-body">{def.body}</div>

            <div className="crt-actions">
              <button type="button" className="crt-btn-back" onClick={onBack} disabled={step === 0}>
                Back
              </button>
              <button type="button" className="crt-btn-skip" onClick={onSkip}>
                Skip tour
              </button>
              <div style={{ flex: 1 }} />
              <button type="button" className="crt-btn-next" onClick={onNext}>
                {isLast ? "Finish tour" : "Next"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const STYLES = `
@keyframes crtPop { 0% { transform: translateY(10px) scale(0.96); opacity: 0; } 100% { transform: translateY(0) scale(1); opacity: 1; } }
@keyframes crtPulse {
  0% { box-shadow: 0 0 0 100vmax rgba(8,12,30,0.62), 0 0 0 2px rgba(91,108,255,0.85), 0 0 20px rgba(91,108,255,0.4); }
  55% { box-shadow: 0 0 0 100vmax rgba(8,12,30,0.62), 0 0 0 2.5px rgba(91,108,255,0.95), 0 0 26px rgba(91,108,255,0.55); }
  100% { box-shadow: 0 0 0 100vmax rgba(8,12,30,0.62), 0 0 0 2px rgba(91,108,255,0.85), 0 0 20px rgba(91,108,255,0.4); }
}
@keyframes crtBar { from { width: 0%; } }
@keyframes crtRing {
  0% { transform: scale(0.98); opacity: 0.4; }
  70% { transform: scale(1.08); opacity: 0.12; }
  100% { transform: scale(1.12); opacity: 0; }
}
@keyframes crtRingDelayed {
  0% { transform: scale(0.98); opacity: 0.4; }
  70% { transform: scale(1.08); opacity: 0.12; }
  100% { transform: scale(1.12); opacity: 0; }
}
@keyframes crtGlow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(91,108,255,0); }
  50% { box-shadow: 0 0 14px 2px rgba(91,108,255,0.3); }
}
.crt-overlay {
  position: fixed; inset: 0; z-index: 2147483600;
  pointer-events: none;
}
.crt-spotlight {
  position: fixed; border-radius: 18px;
  animation: crtPulse 3.2s ease-in-out infinite;
  pointer-events: none; z-index: 1;
  transition: left 240ms ease, top 240ms ease, width 240ms ease, height 240ms ease;
}
.crt-dim {
  position: fixed; inset: 0;
  background: radial-gradient(1200px 700px at 50% 30%, rgba(8,12,30,0.55) 0%, rgba(8,12,30,0.72) 100%);
  z-index: 1; pointer-events: none;
}
.crt-card {
  position: fixed; z-index: 3; pointer-events: auto;
  background: linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%);
  color: #172554; border-radius: 24px;
  border: 1px solid rgba(91,108,255,0.18);
  box-shadow: 0 30px 70px rgba(6,10,25,0.42), 0 8px 26px rgba(6,10,25,0.22);
  overflow-y: auto;
  max-height: calc(100vh - 32px);
  animation: crtPop 260ms ease-out;
}
.crt-card-band { height: 6px; background: linear-gradient(90deg, ${ACCENT}, ${ACCENT2}); }
.crt-card-body { padding: 18px 22px 16px; }
.crt-card-head { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.crt-step-no {
  width: 40px; height: 40px; border-radius: 13px; flex: none;
  background: linear-gradient(135deg, ${ACCENT}, ${ACCENT2});
  color: #fff; display: flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 15px;
  box-shadow: 0 6px 16px rgba(91,108,255,0.4);
}
.crt-card-progress-wrap { flex: 1; min-width: 0; }
.crt-eyebrow {
  font-size: 10.5px; font-weight: 800; letter-spacing: 0.16em; text-transform: uppercase;
  color: ${ACCENT}; margin-bottom: 4px;
}
.crt-progress-track { height: 5px; border-radius: 99px; background: #e8ecf9; overflow: hidden; }
.crt-progress-fill {
  height: 100%; border-radius: 99px;
  background: linear-gradient(90deg, ${ACCENT}, ${ACCENT2});
  transition: width 260ms ease; animation: crtBar 320ms ease;
}
.crt-title { font-size: 18px; font-weight: 800; letter-spacing: -0.01em; margin-bottom: 6px; }
.crt-body { font-size: 13.5px; line-height: 1.58; color: #3e4f6b; margin-bottom: 16px; }
.crt-actions { display: flex; align-items: center; gap: 8px; }
.crt-btn-back {
  border: 1px solid #d8e0f2; background: #ffffff; color: #3b4771;
  border-radius: 12px; padding: 9px 14px; font-size: 13px; font-weight: 650;
  cursor: pointer; box-shadow: 0 1px 2px rgba(6,10,25,0.06);
}
.crt-btn-back:disabled { opacity: 0.45; cursor: default; }
.crt-btn-skip {
  border: none; background: transparent; color: #8b96b8;
  font-size: 12.5px; font-weight: 700; letter-spacing: 0.02em;
  cursor: pointer; padding: 9px 6px;
}
.crt-btn-next {
  border: none; border-radius: 12px; padding: 10px 22px;
  font-size: 13px; font-weight: 750; color: #fff; cursor: pointer;
  box-shadow: 0 8px 20px rgba(91,108,255,0.45);
  background: linear-gradient(135deg, ${ACCENT}, ${ACCENT2});
}
.crt-start-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  border: 1.5px solid rgba(91,108,255,0.55);
  background: linear-gradient(135deg, rgba(91,108,255,0.14), rgba(236,72,153,0.1));
  color: ${ACCENT}; border-radius: 999px; padding: 8px 16px;
  font-size: 13px; font-weight: 750; cursor: pointer;
  animation: crtGlow 2.8s ease-in-out infinite;
}
.crt-start-icon {
  display: inline-flex; width: 18px; height: 18px; border-radius: 50%;
  align-items: center; justify-content: center;
  background: linear-gradient(135deg, ${ACCENT}, ${ACCENT2});
  color: #fff; font-size: 8px; font-weight: 800;
}
`;